// THE SURFACE TRANSITION'S DECISIONS — everything the controller works OUT, with nothing it DOES.
//
// transition-controller.ts is the imperative half: it appends panes, writes transforms, hands keyframes to the
// compositor and drops the covering layer at the end. This file is the other half — the questions it asks on
// the way. It exists for the same reason nav-stack-machine.ts does, and the argument transfers almost word for
// word, because THE BUGS THIS ENGINE HAS SHIPPED ARE ORDERINGS. Every one in its history is two correct facts
// arriving on different frames, and the code that had to choose between them chose on one:
//
//   • a link CLICKED DURING THE SPRING. The router moves at once; the settle knows nothing about it and lands a
//     beat later still believing it owes a navigation to its own destination. Committing there stomps the page
//     the user explicitly asked for (`resolveFinish`'s supersede clauses — `expected`).
//   • …and the same collision one step further on: the settle has nothing to commit, but the covering pane is
//     now hiding a route the router will NEVER reach, because it went somewhere else. Held to the watchdog that
//     is a full second of frozen screen (`resolveLiveCommit`, `'superseded'`).
//   • an intermediate route rendering underneath while the commit we ARE waiting for is still in flight — which
//     means nothing and must not drop the pane (`resolveLiveCommit`, `'hold'`).
//   • a second navigation landing on a slide already running: the same destination again, back where it came
//     from, or a third route — three different answers, and only the third may pay for a full-page clone
//     (`resolveInterrupt`).
//
// NONE OF THAT IS VISIBLE IN A BROWSER TEST, and the reason is sharper here than in the stack's case: at every
// one of those instants an OPAQUE PANE COVERS THE VIEWPORT. That is the whole design — the route swap has to
// happen behind something — so a screenshot of the right answer and a screenshot of the wrong one are the same
// screenshot. What separates them is which page is underneath when the pane is finally dropped, one frame later.
// The visible symptom of getting it wrong is a screen that looks correct and is a second stale, or a navigation
// that "didn't happen"; the only honest e2e signal is the rolling event log, which is why those specs read it.
//
// As DATA the whole thing is a handful of strings and a number, and the lifecycle is a fixed sequence of events
// over them — so it can be replayed exactly, and every state it passes through checked (transition-machine.
// unit.test.ts).
//
// THE GEOMETRY IS HERE FOR A RELATED REASON, not merely because it is arithmetic. `shadowFade` is zero at BOTH
// endpoints, `stripOpacity`'s lateral hairline is gone at both, and a parked/at-rest pane sits exactly where the
// CSS would have put it anyway: the interesting values all live strictly between the two frames anyone can
// screenshot. A settle sampled along its own trajectory is checkable as numbers and nowhere else.
//
// PURE, AND IMPORTS ONLY THE SHARED MOTION TUNING PLUS THE SURFACE CONTRACT. Same rule as the stack's two
// modules: reachable from a plain relative import, and it drags neither React, the router nor the DOM in behind
// it. `surface.config.ts` is itself pure (it is the shared contract both engines read), so importing
// `matchesFrom` and the phase union costs nothing.
//
// WHAT DELIBERATELY STAYED IMPERATIVE, so the split is not re-litigated file by file: creating/appending/
// removing pane and shadow elements, every style write, `Element.animate` and its cancellation, rAF and timer
// scheduling, snapshot capture, handing React its preview pane back, the orphan sweep in `reconcile`, and the
// event log. Those are effects with no answer to check — a test that asserted them would be asserting that the
// DOM API was called, which the e2e specs already prove end to end.

import { TRANSITION_CONFIG } from '../core/motion.config'
import { sampleIndexAt, SETTLE_EPSILON, SPRING_HZ, springStep, type SpringState } from '../core/spring'
import { matchesFrom, type TransitionEdge, type TransitionPhase } from './surface.config'

const { parallax, commitThreshold, edgeWidth, flickVelocity, intentSlop, shadow } = TRANSITION_CONFIG

/** Which side of the transition supplied the panes — a finger drag, or a Link/back-forward already committed. */
export type TransitionMode = 'gesture' | 'programmatic'

/* ── 1. GEOMETRY: where the panes and the seam are at a given progress ───────────────────────────────────── */

/** `progress` is the surface's one source of truth (0 = origin covering, 1 = destination covering), and it is
 *  never allowed outside that range — an overshoot past 1 is a CHAIN onto the next page, not a bigger number. */
export function clampProgress(p: number): number {
  return p < 0 ? 0 : p > 1 ? 1 : p
}

/**
 * How present the leading-edge shadow is at this progress: a half-sine, so it's zero at BOTH endpoints and
 * fullest mid-travel. The shadow therefore eases in as the top pane lifts away from its edge and eases back
 * out as it lands. Zero at the ends is the point — at rest the pane is flush against the edge with nothing to
 * cast onto, and a shadow that's simply switched on when the transition arms and stripped at teardown is
 * visible doing both.
 */
export function shadowFade(progress: number): number {
  return Math.sin(Math.PI * clampProgress(progress))
}

/** The strip's width — the shadow's gradient band, or the lateral separator's hairline. */
export function stripWidth(lateral: boolean): number {
  return lateral ? 1 : shadow.width
}

/**
 * The strip's presence at `progress`: the shadow eases in/out (see shadowFade); the lateral separator is
 * simply THERE while a seam is on screen, and gone at the endpoints where the seam meets the stage edge.
 */
export function stripOpacity(lateral: boolean, progress: number): number {
  if (!lateral) return shadowFade(progress)
  return progress <= 0.001 || progress >= 0.999 ? 0 : 1
}

/**
 * Where each pane's box sits, as a function of progress — the ONE place the edge decides direction and z-order.
 *
 * `topIsIncoming` is what the two edges actually differ by, and naming it is the point: on `'right'` the
 * arriving page rides ON TOP and enters from the right, so the top pane is the incoming one; on `'left'` the
 * page being LEFT slides off to the right on top and the incoming one is revealed underneath. Every downstream
 * question — which element gets z-index 2, which one the seam rides, which one a chain snapshots — is that
 * boolean, and reading it off the edge string in three places is how a third edge would silently take the
 * hierarchical branch.
 */
export interface PaneGeometry {
  readonly topIsIncoming: boolean
  readonly topX: (p: number) => number
  readonly bottomX: (p: number) => number
}

/**
 * `trail` is the whole of what the KIND changes about the travel: a LATERAL (peer) surface has no
 * "underneath", so both panes travel the full distance side by side (factor 1) and the depth cue is absent by
 * construction rather than by a second branch somewhere else.
 */
export function paneGeometry(edge: TransitionEdge, lateral: boolean, width: number): PaneGeometry {
  const trail = lateral ? 1 : parallax
  if (edge === 'right') {
    return { topIsIncoming: true, topX: (p) => (1 - p) * width, bottomX: (p) => -p * trail * width }
  }
  return { topIsIncoming: false, topX: (p) => p * width, bottomX: (p) => -(1 - p) * trail * width }
}

/** The seam's own x: parked flush against the OUTSIDE of the top pane's leading edge, so it darkens the pane
 *  beneath and never bleeds over the pane that casts it. */
export function stripX(topX: number, lateral: boolean): number {
  return topX - stripWidth(lateral)
}

/* ── 2. THE FINGER: what a drag decides, and what it hands to the release ────────────────────────────────── */

/**
 * Has this finger MOVED the panes, as opposed to resting on them?
 *
 * Measured from where the press took hold and against the SAME slop a fresh gesture uses to claim the pointer,
 * in the same pixels: a tap's tremor is a couple of px and must not count as a drag, or catching a settle to
 * tap it would go dead on the shakiest thumbs. A fresh drag is moved by construction (it only arms once
 * horizontal intent is decisive); a CATCH starts unmoved, which is what lets a tap on a caught transition
 * reach the page it landed on.
 */
export function fingerHasMoved(grabProgress: number, progress: number, width: number): boolean {
  return Math.abs(progress - grabProgress) * width >= intentSlop
}

/**
 * Last-resort backstop for a drag, NOT a limit on how long you may hold one. Resting mid-swipe is a first-
 * class thing to do — you hold the pane still and watch the destination finish loading in it — so this has to
 * sit far beyond any human pause. A lost pointer release is caught by real signals instead (window
 * pointerup/pointercancel, a move with no buttons down, blur, and the stale-gesture check on the next press);
 * this only catches the pathological case where a wedged drag is left covering the screen with no input at all.
 */
export const DRAG_WATCHDOG_MS = 60_000

/**
 * Bounds on a drag glide segment's duration (see the controller's `glideToward`). A segment nominally lasts one
 * input interval — the time until the next pointer sample replaces it — measured from the actual event cadence.
 * The floor absorbs timestamp jitter on 120Hz-input devices (where a segment this short is equivalent to the
 * old direct write anyway); the ceiling keeps the pane from drifting in slow motion toward a sample that
 * arrived after a long stall (a finger resting, a dropped frame) — past ~2 frames it should just go there.
 */
const DRAG_SEGMENT_MIN_MS = 8
const DRAG_SEGMENT_MAX_MS = 34
/** Segment duration before any cadence has been measured (one 60Hz input interval). */
const DRAG_SEGMENT_DEFAULT_MS = 17

/**
 * How long the next glide segment should last, from the gap since the previous pointer sample.
 *
 * The out-of-range fallback is not the ceiling: a gap of more than two ceilings is not a slow cadence, it is a
 * finger that STOPPED — the previous segment has long since filled forwards to the finger — so the next sample
 * is a fresh start and gets the nominal interval, not a 34ms crawl toward a position it is already at.
 */
export function dragSegmentMs(elapsedMs: number): number {
  if (elapsedMs <= 0 || elapsedMs > DRAG_SEGMENT_MAX_MS * 2) return DRAG_SEGMENT_DEFAULT_MS
  return Math.min(DRAG_SEGMENT_MAX_MS, Math.max(DRAG_SEGMENT_MIN_MS, elapsedMs))
}

/** One glide segment: the progress span it travels, and the document-timeline anchor it was started at. */
export interface DragSegment {
  readonly from: number
  readonly to: number
  readonly dur: number
  readonly startT: number
}

/**
 * Where a composited drag has VISUALLY got to — the running segment sampled at its own anchored clock.
 *
 * Never the Animation's `currentTime`: a just-issued segment is play-pending and the browser assigns its start
 * time at a later rendering update, so that readback was nondeterministically 0–2 events stale and each
 * replacement segment yanked the panes back before re-travelling (visible jitter). Self-anchored start times
 * make consecutive segments join exactly, and make this reconstruction arithmetic rather than a guess.
 */
export function segmentProgressAt(seg: DragSegment | null, now: number, fallback: number): number {
  if (!seg) return fallback
  const t = now - seg.startT
  const k = seg.dur <= 0 ? 1 : t < 0 ? 0 : t > seg.dur ? 1 : t / seg.dur
  return seg.from + (seg.to - seg.from) * k
}

/** Which way the finger must travel to move this transition FORWARD (toward progress 1), in screen x. */
export function edgeSign(edge: TransitionEdge): -1 | 1 {
  return edge === 'right' ? -1 : 1
}

/**
 * WHERE A RELEASE IS AIMED — the commit decision, and the only place a drag becomes a navigation.
 *
 * A flick wins outright in either direction, however far the drag actually got; a release with no decisive
 * speed falls back to distance. Deliberately asymmetric about the threshold (`> commitThreshold`, so exactly
 * at it springs back): the fraction is what the user has ALREADY dragged, and the tie belongs to "no-op",
 * because a cancelled gesture costs nothing and a wrong commit costs a navigation.
 */
export function releaseTarget(edge: TransitionEdge, velocityX: number, progress: number): 0 | 1 {
  const towardCommit = edgeSign(edge) * velocityX
  if (towardCommit > flickVelocity) return 1
  if (towardCommit < -flickVelocity) return 0
  return progress > commitThreshold ? 1 : 0
}

/** The release's speed in the spring's own units (progress per second) — px/ms of finger over a stage width. */
export function releaseVelocity(edge: TransitionEdge, velocityX: number, width: number): number {
  return (edgeSign(edge) * velocityX * 1000) / width
}

/* ── 3. THE SETTLE: the spring, its fallback integration, and how long it may take ───────────────────────── */

/** How long a settle takes, from the number of samples the spring was integrated into. */
export function settleDurationMs(sampleCount: number): number {
  return (sampleCount / SPRING_HZ) * 1000
}

/** The default backstop for a phase that is not a drag — settles are ~300ms, so this is well beyond. */
export const SETTLE_WATCHDOG_MS = 1200

/** The backstop must OUTLIVE the settle it is guarding, or a long spring (a re-aim from far out) would be torn
 *  down while it was still perfectly healthy. */
export function settleWatchdogMs(durationMs: number): number {
  return Math.max(SETTLE_WATCHDOG_MS, durationMs + 600)
}

/**
 * The settle's current speed (progress units / second), read off the precomputed trajectory rather than
 * differentiated from the DOM — for re-aiming a running spring without losing momentum.
 */
export function sampleVelocityAt(samples: readonly number[], elapsedMs: number): number {
  if (samples.length < 2) return 0
  const index = sampleIndexAt(samples, elapsedMs)
  const here = samples[index]
  const next = samples[Math.min(index + 1, samples.length - 1)]
  if (here === undefined || next === undefined) return 0
  return (next - here) * SPRING_HZ
}

/** The largest step the fallback integrator will take (one 30Hz frame). A tab restored after a minute in the
 *  background delivers one enormous rAF delta, and integrating it whole launches the pane off the stage. */
const MAX_FRAME_S = 1 / 30

export function frameDelta(now: number, lastFrame: number): number {
  return Math.min((now - lastFrame) / 1000, MAX_FRAME_S)
}

/**
 * One step of the main-thread spring — the fallback path, used only where `Element.animate` is unavailable.
 * `arrived` is the same position test spring.ts breaks its sampling on, so the two paths land identically.
 */
export function integrateSpring(state: SpringState, target: 0 | 1, dt: number): SpringState & { arrived: boolean } {
  const next = springStep(state.progress, state.velocity, target, dt)
  return { ...next, arrived: Math.abs(next.progress - target) < SETTLE_EPSILON }
}

/* ── 4. REACHABILITY: exactly one of the two rendered apps is live ───────────────────────────────────────── */

/**
 * Is the LIVE `<Outlet/>` layer inert right now?
 *
 * A transition puts two renders of the app in the document at once, and the rule is symmetric — exactly one
 * pane is reachable by pointer, keyboard and screen reader. The live layer is the ORIGIN, sitting behind both
 * panes, so it is inert whenever an incoming pane covers it…
 *
 * …EXCEPT while it holds the focused element. Inerting the subtree the user is typing in would blur it, and a
 * cancelled gesture is supposed to be a pure no-op — it must not be able to take focus away from the page it
 * never left.
 */
export function liveLayerInert(phase: TransitionPhase, hasIncomingPane: boolean, holdsFocus: boolean): boolean {
  return phase !== 'idle' && hasIncomingPane && !holdsFocus
}

/** True when a programmatic slide is mid-flight and a new navigation can re-aim/reverse it. */
export function canRetarget(mode: TransitionMode, phase: TransitionPhase): boolean {
  return mode === 'programmatic' && phase === 'settling'
}

/**
 * True whenever a transition is in flight and can be CAUGHT — the iOS behaviour: put a finger down on a moving
 * transition and it stops dead under you, hold it anywhere, drag it either way, let go and it resolves from
 * there. It deliberately does not ask how the slide started, nor whether the URL has committed yet:
 * `resolveFinish` reconciles the router with wherever you drop it.
 */
export function canRegrab(phase: TransitionPhase): boolean {
  return phase === 'settling' || phase === 'finishing'
}

/* ── 5. THE CHROME CLOCK: what the header and the underline read off a travel ────────────────────────────── */

/**
 * Which path the nav chrome highlights at this progress — the midpoint flip, decoupled from the URL entirely.
 *
 * At 0.5 exactly the DESTINATION wins, and that is not a rounding preference: the haptic's forward edge fires
 * on the same comparison (see createMidpointHaptic), so the tick and the highlight are the same event. A `>`
 * here would let the two disagree by one frame on a drag parked precisely on the seam.
 */
export function visualPathAt(progress: number, originPath: string, destPath: string): string {
  return progress >= 0.5 ? destPath : originPath
}

/**
 * May this surface blank the SHARED chrome stores on teardown?
 *
 * They are module-global and outlets NEST, so a controller disposed mid-slide of a SIBLING surface (the admin
 * tab pager unmounting while the root surface glides) must not blank that slide's underline and active link
 * mid-animation. Ownership is the pair, not the surface: whoever the running travel names is the one entitled
 * to clear it, and a store with nothing running is free.
 */
export function ownsRunningChrome(
  running: { from: string; to: string } | null,
  originPath: string,
  destPath: string,
): boolean {
  return !running || (running.from === originPath && running.to === destPath)
}

/* ── 6. THE ORDERINGS: three decisions taken against facts that arrive out of order ──────────────────────── */

/**
 * A navigation landed while a link slide is still running.
 *
 * Because BOTH panes are stable — a snapshot of the origin and a live preview of the destination, neither of
 * which is the live `<Outlet/>` — an interruption is almost always just the spring changing its mind. The three
 * answers are not interchangeable, and the third is the expensive one:
 *
 *   • `'retarget'` — heading to the same place again. Keep going; re-aim to 1.
 *   • `'reverse'`  — heading back where we came from. Re-aim to 0; the origin's pane is already sitting there,
 *                    so nothing swaps and the pane simply changes its mind without jumping.
 *   • `'redirect'` — a third route entirely. Re-arm from scratch, which costs a deep clone of the live page.
 *
 * Why the split matters beyond correctness: retarget and reverse are the RAPID back/forward moments where a
 * dropped frame is most visible, and they must not pay a full-page clone just to throw it away. (The previous
 * design used the live layer as the incoming pane, so a reverse had to swap a freshly-frozen snapshot into its
 * slot — and bred a whole class of which-pane-is-which bugs. Panes that never change roles are what make a
 * reverse a pure re-aim.)
 */
export type InterruptAction = 'retarget' | 'reverse' | 'redirect'

export function resolveInterrupt(running: { from: string; to: string }, nextTo: string): InterruptAction {
  if (nextTo === running.to) return 'retarget'
  if (nextTo === running.from) return 'reverse'
  return 'redirect'
}

/**
 * What `finish` must do when the animation lands on an endpoint (0 = origin, 1 = destination).
 *
 * `'abandon'`         — there is no route this landed on at all; just drop the panes. (The only outcome that
 *                       leaves `committed` alone — the controller has nothing to record.)
 * `'navigate'`        — the router is not where the picture is; take it there and hold the covering pane.
 * `'await-live'`      — the router is already on its way; hold the pane until the live layer actually shows it.
 * `'reveal'`          — the live layer is already showing it; drop the panes now.
 * `'superseded-hold'` — a FOREIGN navigation landed mid-settle, heading exactly where this slide landed. There
 *                       is nothing to commit, but the picture is right: hold and let the live layer release it.
 * `'superseded-drop'` — a foreign navigation landed heading somewhere ELSE. Get out of its way at once.
 */
export type FinishAction =
  | { kind: 'abandon' }
  | { kind: 'navigate'; landedOn: string; mark: 'finish→commit' | 'finish→back' }
  | { kind: 'await-live'; landedOn: string }
  | { kind: 'reveal'; mark: 'finish→settled' | 'finish→cancel' }
  | { kind: 'superseded-hold'; landedOn: string; current: string }
  | { kind: 'superseded-drop'; landedOn: string; current: string }

export interface FinishInput {
  /** The endpoint the animation landed on. */
  target: 0 | 1
  /** Whether the ROUTER already sits on the destination. A gesture starts false (it only commits if you
   *  complete it); a link/back-forward slide starts true. */
  committed: boolean
  originPath: string
  /** The running transition's destination, or '' if there is no transition left to name one. */
  destPath: string
  /** The route the live `<Outlet/>` currently holds, as last reported by the outlet. */
  livePath: string
  /** The router's CURRENT committed pathname, read at this instant. */
  location: string
}

/**
 * BRING THE ROUTER TO WHERE THE PICTURE IS — and only if it isn't already there. Stated that way the four base
 * cases fall out symmetrically, which is what lets an in-flight transition be grabbed and dragged to either end
 * no matter how it started:
 *
 *   • gesture completed       (¬committed → 1): navigate to the destination.
 *   • gesture cancelled       (¬committed → 0): nothing to undo — just drop the panes.
 *   • link slide let run      ( committed → 1): the router is already there — drop the panes.
 *   • link slide dragged back ( committed → 0): navigate BACK to the origin.
 *
 * THE TWO SUPERSEDE CLAUSES ARE THE ORDERING, and they are separate questions asked of the same fact:
 *
 *   BEFORE ISSUING OUR OWN NAVIGATION, the router must still sit where this transition believes it does — a
 *   commit expects it on the origin (a gesture never moves the URL), a revert expects it on the slide's
 *   destination. A mismatch means a foreign navigation landed mid-settle: a link clicked during the spring,
 *   which gesture mode cannot retarget, so the outlet dropped the visuals and the router ran it alone. The
 *   user's explicit navigation wins; never commit the stale destination on top of it.
 *
 *   WITH NOTHING TO NAVIGATE, the wait itself is the risk. The router may simply not have painted `landedOn`
 *   yet, in which case the covering pane is doing its job. But if it is no longer BOUND for `landedOn`, that
 *   pane is now hiding a page the user asked for and no live-layer report will ever release it — the watchdog
 *   would, a full second later, which is a second of frozen screen.
 */
export function resolveFinish(input: FinishInput): FinishAction {
  const landedOn = input.target === 1 ? input.destPath : input.originPath
  if (!landedOn) return { kind: 'abandon' }

  const needsNavigation = input.target === 1 ? !input.committed : input.committed
  if (needsNavigation) {
    const expected = input.target === 1 ? input.originPath : input.destPath
    if (expected && !matchesFrom(input.location, expected)) {
      return matchesFrom(input.location, landedOn)
        ? { kind: 'superseded-hold', landedOn, current: input.location }
        : { kind: 'superseded-drop', landedOn, current: input.location }
    }
    return { kind: 'navigate', landedOn, mark: input.target === 1 ? 'finish→commit' : 'finish→back' }
  }

  if (!matchesFrom(input.livePath, landedOn)) {
    if (!matchesFrom(input.location, landedOn)) {
      return { kind: 'superseded-drop', landedOn, current: input.location }
    }
    return { kind: 'await-live', landedOn }
  }
  return { kind: 'reveal', mark: input.target === 1 ? 'finish→settled' : 'finish→cancel' }
}

/**
 * What a live-layer commit report means while we are holding a covering pane.
 *
 * Teardown drops the panes and reveals that layer, so it is only safe once the layer actually holds the route
 * the panes landed on — otherwise the slide would end by revealing the page you just left. Waiting on THIS,
 * rather than on the navigation promise, is what lets the animation start before the router has rendered
 * anything at all.
 *
 * `'ignore'`     — nothing is being held; the report is just bookkeeping.
 * `'hold'`       — a route we are NOT waiting for rendered underneath, but the router is still bound for the
 *                  one we are (its location moves at the START of a navigation), so this means nothing.
 * `'superseded'` — …and the case that made this a decision rather than an equality check: the router has gone
 *                  somewhere else, so the pane is hiding a page the user asked for. `interactiveIncoming` made
 *                  it reachable by TAPPING — a link on the arriving pane, tapped in the window between the
 *                  commit and the reveal — but the hole was always there for any navigation that lands in it
 *                  (keyboard, programmatic), so the fix is not gated on the flag.
 * `'reveal'`     — the awaited route is on the live layer; the covering pane may go.
 */
export type LiveCommitAction = 'ignore' | 'hold' | 'superseded' | 'reveal'

export function resolveLiveCommit(input: {
  phase: TransitionPhase
  awaiting: string | null
  path: string
  location: string
}): LiveCommitAction {
  if (input.phase !== 'finishing' || !input.awaiting) return 'ignore'
  if (matchesFrom(input.path, input.awaiting)) return 'reveal'
  return matchesFrom(input.location, input.awaiting) ? 'hold' : 'superseded'
}

/* ── 7. THE POINTER: what a press has to decide before any of the above applies ──────────────────────────── */

/**
 * Is a press at `x` (px from the stage's left, stage width `width`) inside the start strip for a transition on
 * `edge`? `edgeOnly` is the iOS back-swipe shape; pager mode — the default — starts anywhere.
 */
export function startsInEdge(edge: TransitionEdge, x: number, width: number, edgeOnly: boolean): boolean {
  if (!edgeOnly) return true
  return edge === 'left' ? x <= edgeWidth : x >= width - edgeWidth
}

/**
 * WHICH TRANSITION THIS PRESS MEANT, or null while it is still undecided.
 *
 * Built to keep a real finger's gesture alive, and the asymmetry is the whole point: this only ever ACCEPTS.
 * A finger never travels on a perfect axis — it starts diagonally and wobbles, and a SLOW drag wobbles most —
 * so rejecting on the first sample where `dy > dx` is exactly what made slow swipes feel dead: one stray
 * vertical pixel and the drag was over for the rest of the press. Undecided presses stay undecided, and the
 * next samples get a say.
 *
 * Nothing is hijacked from the page by waiting: the surface is `touch-action: pan-y`, so a genuine vertical
 * scroll is taken over by the browser, which cancels the pointer stream. Requiring horizontal to DOMINATE
 * (`ax > ay`, not merely `ax >= intentSlop`) is what keeps this out of that scroll's way.
 *
 * The direction then picks the edge: a leftward drag pulls the NEXT page in from the right.
 */
export function resolveIntent(dx: number, dy: number): TransitionEdge | null {
  const ax = Math.abs(dx)
  if (ax < intentSlop || ax <= Math.abs(dy)) return null
  return dx < 0 ? 'right' : 'left'
}

/**
 * The release-velocity estimate: an exponential moving average of the per-sample speed, weighted toward
 * HISTORY (0.6/0.4).
 *
 * A flick's last sample is the one most likely to be a runt — the finger is already leaving the glass, and on
 * a throttled main thread the final pointermove can land a whole frame late — so a raw "last delta over last
 * interval" reads a genuine fling as a dead release about as often as not. Weighting the past damps that
 * without lagging a real direction change, which the drag itself is showing anyway.
 */
export function blendVelocity(previous: number, instant: number): number {
  return previous * 0.6 + instant * 0.4
}

/**
 * Where the panes should be for a finger at `clientX` — 1:1 with it, anchored at the point the press STARTED.
 *
 * Anchoring to the press point rather than to the point intent was locked at is deliberate: rebasing at the
 * lock would swallow the slop distance, so the pane would sit still for the first few pixels and only then
 * start moving — a dead zone that reads as "it doesn't respond until a threshold". `baseProgress` is what makes
 * a RE-GRAB seamless: the finger picks the pane up wherever the settle had got to, rather than at 0.
 */
export function dragProgress(
  base: { progress: number; sign: -1 | 1; x: number },
  clientX: number,
  width: number,
): number {
  return base.progress + (base.sign * (clientX - base.x)) / width
}

/**
 * Pushed PAST the destination: how much travel is left over for the NEXT transition, or null if the finger is
 * still inside this one.
 *
 * This is what lets you keep flipping — grab a transition that is still settling, keep dragging the same way,
 * and carry on to the page after it instead of jamming against progress 1 and waiting out a settle. The
 * overshoot is carried into the new transition, so the pane stays exactly under the finger across the handover.
 */
export function chainOvershoot(progress: number): number | null {
  return progress > 1 ? progress - 1 : null
}
